
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { FileText, BookOpen, Clipboard, ScrollText } from "lucide-react";

interface DocumentCardProps {
  title: string;
  description: string;
  icon: string;
  onClick: () => void;
}

export const DocumentCard = ({ title, description, icon, onClick }: DocumentCardProps) => {
  const getIcon = () => {
    switch (title.toLowerCase()) {
      case "ofício":
        return <FileText className="w-6 h-6 md:w-8 md:h-8 text-primary" />;
      case "requerimento":
        return <Clipboard className="w-6 h-6 md:w-8 md:h-8 text-primary" />;
      case "discurso":
        return <BookOpen className="w-6 h-6 md:w-8 md:h-8 text-primary" />;
      case "memorando":
        return <ScrollText className="w-6 h-6 md:w-8 md:h-8 text-primary" />;
      default:
        return null;
    }
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
      className="h-full"
    >
      <Card 
        className="group relative overflow-hidden p-4 md:p-6 h-full transition-all duration-300 cursor-pointer glass-card hover:shadow-lg hover:shadow-primary/10 rounded-2xl border-primary/5"
        onClick={onClick}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        <div className="relative z-10 flex flex-col items-center justify-between h-full space-y-4">
          <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5 group-hover:from-primary/20 group-hover:to-primary/10 transition-colors duration-300 shadow-inner">
            {getIcon()}
          </div>
          
          <div className="space-y-2 text-center flex-grow">
            <h3 className="text-lg md:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700">
              {title}
            </h3>
            <p className="text-sm md:text-base text-gray-600 leading-relaxed">
              {description}
            </p>
          </div>

          <Button 
            className="w-full bg-primary hover:bg-primary-dark text-white font-medium transition-all duration-300 group relative overflow-hidden rounded-xl h-10 shadow-lg shadow-primary/20"
            onClick={onClick}
          >
            <span className="relative z-10 flex items-center gap-2 text-sm">
              Criar {title}
              <motion.span
                initial={{ x: -5, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                →
              </motion.span>
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-primary to-primary-dark transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
          </Button>
        </div>
      </Card>
    </motion.div>
  );
};
